import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Pencil, Trash2, Plus } from 'lucide-react';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import { getPaletteColor } from '@/core/lib/colors';
import { cn } from '@/core/lib/utils';
import { useBoardStatuses } from '../hooks/useBoardStatuses';
import {
  useUpdateBoardStatus,
  useDeleteBoardStatus,
  useReorderBoardStatuses,
} from '../hooks/useBoardStatusMutations';
import { BoardStatusFormDialog } from './BoardStatusFormDialog';
import type { BoardStatus, StatusType } from '../types';

const STATUS_GROUPS: StatusType[] = ['Todo', 'InProgress', 'Done'];

interface BoardStatusesManagerProps {
  boardId: string;
}

export function BoardStatusesManager({ boardId }: BoardStatusesManagerProps) {
  const { t } = useTranslation('boards');
  const { toast } = useToast();
  const { statuses, isLoading } = useBoardStatuses(boardId);
  const updateStatus = useUpdateBoardStatus(boardId);
  const deleteStatus = useDeleteBoardStatus(boardId);
  const reorderStatuses = useReorderBoardStatuses(boardId);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<BoardStatus | undefined>(undefined);

  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const targets = location.current.dropTargets;
        if (targets.length === 0) return;

        const sourceData = source.data as { statusId?: string; groupType?: StatusType };
        if (!sourceData.statusId || !sourceData.groupType) return;

        const sourceId = sourceData.statusId;
        const sourceGroup = sourceData.groupType;

        // Innermost target: item-level when dropped on an item, group-level on empty space
        const innerData = targets[0].data as { statusId?: string; groupType?: StatusType };
        if (!innerData.groupType) return;

        const targetGroup = innerData.groupType;

        // No-op: dropped on itself
        if (innerData.statusId === sourceId) return;

        const sourceStatus = statuses.find((s) => s.id === sourceId);
        if (!sourceStatus) return;

        // Build the new ordered list for the target group without the source item
        const base = statuses
          .filter((s) => s.type === targetGroup && s.id !== sourceId)
          .slice()
          .sort((a, b) => a.order - b.order);

        if (innerData.statusId) {
          // Insert before the target item
          const idx = base.findIndex((s) => s.id === innerData.statusId);
          base.splice(idx === -1 ? base.length : idx, 0, sourceStatus);
        } else {
          // Dropped on empty group space — append to end
          base.push(sourceStatus);
        }

        const orderedIds = base.filter((s) => s.id != null).map((s) => s.id!);

        if (sourceGroup !== targetGroup) {
          updateStatus.mutate({ statusId: sourceId, data: { type: targetGroup } });
        }

        reorderStatuses.mutate({ orderedIds });
      },
    });
  }, [statuses, updateStatus, reorderStatuses]);

  async function handleDelete(status: BoardStatus) {
    if (!status.id) return;
    const result = await confirm({
      title: t('statuses.delete.confirmTitle'),
      description: t('statuses.delete.confirmDescription'),
      confirmLabel: t('actions.delete', { ns: 'boards' }),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    try {
      await deleteStatus.mutateAsync(status.id);
      toast.success({ title: t('statuses.toast.deleteSuccess') });
    } catch {
      // global handler shows error toast
    }
  }

  function handleEdit(status: BoardStatus) {
    setEditingStatus(status);
    setDialogOpen(true);
  }

  function handleCreate() {
    setEditingStatus(undefined);
    setDialogOpen(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="boards-statuses-manager">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="boards-statuses-manager">
      <div className="flex justify-end">
        <Button size="sm" onClick={handleCreate} data-testid="boards-status-create-button">
          <Plus className="mr-1.5 h-4 w-4" />
          {t('statuses.actions.create')}
        </Button>
      </div>

      {STATUS_GROUPS.map((group) => (
        <StatusGroupSection
          key={group}
          group={group}
          statuses={statuses.filter((s) => s.type === group)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      {dialogOpen && (
        <BoardStatusFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          boardId={boardId}
          status={editingStatus}
        />
      )}

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          data-testid="boards-status-delete-confirm"
        />
      )}
    </div>
  );
}

const GROUP_TESTID: Record<StatusType, string> = {
  Todo: 'boards-status-group-todo',
  InProgress: 'boards-status-group-inprogress',
  Done: 'boards-status-group-done',
};

const GROUP_LABEL_KEY: Record<StatusType, string> = {
  Todo: 'statuses.groups.todo',
  InProgress: 'statuses.groups.inProgress',
  Done: 'statuses.groups.done',
};

interface StatusGroupSectionProps {
  group: StatusType;
  statuses: BoardStatus[];
  onEdit: (status: BoardStatus) => void;
  onDelete: (status: BoardStatus) => void;
}

export function StatusGroupSection({ group, statuses, onEdit, onDelete }: StatusGroupSectionProps) {
  const { t } = useTranslation('boards');
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    return dropTargetForElements({
      element: el,
      getData: () => ({ groupType: group }),
    });
  }, [group]);

  return (
    <div
      ref={dropRef}
      data-testid={GROUP_TESTID[group]}
      className="rounded-lg border border-border"
    >
      <div className="flex items-center gap-2 rounded-t-lg bg-muted/50 px-3 py-2">
        <span className="text-sm font-semibold">{t(GROUP_LABEL_KEY[group])}</span>
        <span className="text-xs text-muted-foreground">({statuses.length})</span>
      </div>
      <div className="divide-y divide-border">
        {statuses.length === 0 ? (
          <p className="px-3 py-3 text-xs text-muted-foreground">{t('statuses.empty')}</p>
        ) : (
          statuses
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((status) => (
              <StatusItem
                key={status.id}
                status={status}
                group={group}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
        )}
      </div>
    </div>
  );
}

interface StatusItemProps {
  status: BoardStatus;
  group: StatusType;
  onEdit: (status: BoardStatus) => void;
  onDelete: (status: BoardStatus) => void;
}

export function StatusItem({ status, group, onEdit, onDelete }: StatusItemProps) {
  const { t } = useTranslation('boards');
  const itemRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const colorValue = status.color ? getPaletteColor(status.color)?.value : undefined;

  useEffect(() => {
    const el = itemRef.current;
    const handle = handleRef.current;
    if (!el || !handle || !status.id) return;
    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ statusId: status.id, groupType: group }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
    const cleanupDrop = dropTargetForElements({
      element: el,
      getData: () => ({ statusId: status.id, groupType: group }),
    });
    return () => {
      cleanupDraggable();
      cleanupDrop();
    };
  }, [status.id, group]);

  return (
    <div
      ref={itemRef}
      data-testid={`boards-status-item-${status.id}`}
      className={cn(
        'flex items-center gap-2 px-3 py-2.5 transition-opacity',
        isDragging && 'opacity-40',
      )}
    >
      <button
        type="button"
        ref={handleRef}
        data-testid={`boards-status-${status.id}-drag-handle`}
        className="cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
        aria-label="Drag to reorder"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="size-4" />
      </button>

      <div
        className="h-3 w-3 shrink-0 rounded-full border border-border"
        style={colorValue ? { background: colorValue } : undefined}
      />

      <span className="flex-1 truncate text-sm">{status.name ?? '—'}</span>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEdit(status)}
            aria-label={t('statuses.actions.edit')}
            data-testid={`boards-status-${status.id}-edit-button`}
          >
            <Pencil className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('statuses.actions.edit')}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(status)}
            aria-label={t('statuses.actions.delete')}
            data-testid={`boards-status-${status.id}-delete-button`}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('statuses.actions.delete')}</TooltipContent>
      </Tooltip>
    </div>
  );
}
